import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import {
  getNotificationChannels,
  createNotificationChannel,
  updateNotificationChannel,
  deleteNotificationChannel,
  getNotificationRules,
  createNotificationRule,
  updateNotificationRule,
  deleteNotificationRule,
  getNotificationConfig,
  updateNotificationConfig,
  testNotificationChannel,
  type NotificationChannel,
  type NotificationRule,
} from '@/api/notifications';
import { useAuthStore } from '@/store/auth';
import { listUsers } from '@/api/users';
import { AssistantSettings } from '@/components/AssistantSettings';

const EVENT_LABELS: Record<NotificationRule['event_type'], string> = {
  dep: 'Deposit (dep / sale)',
  reg: 'Registration (reg)',
  all: 'Any conversion',
  ban: 'App banned / restored',
  summary: 'Offer summary (once at N regs)',
};

export function Notifications() {
  const queryClient = useQueryClient();
  const admin = useAuthStore((s) => s.admin);
  const isSuper = admin?.role === 'super_admin';

  const { data: channels = [] } = useQuery({ queryKey: ['notif-channels'], queryFn: getNotificationChannels });
  const { data: rules = [] } = useQuery({ queryKey: ['notif-rules'], queryFn: getNotificationRules });
  const { data: config } = useQuery({ queryKey: ['notif-config'], queryFn: getNotificationConfig, enabled: isSuper });
  const { data: appOwners = [] } = useQuery({
    queryKey: ['users', 'notification-owners'],
    queryFn: () => listUsers().then((result) => result.users),
    enabled: isSuper,
  });

  const inval = () => {
    queryClient.invalidateQueries({ queryKey: ['notif-channels'] });
    queryClient.invalidateQueries({ queryKey: ['notif-rules'] });
    queryClient.invalidateQueries({ queryKey: ['notif-config'] });
  };

  // ─── Bot token / kill switch ────────────────────────────────
  const [tokenInput, setTokenInput] = useState('');
  const [tokenMessage, setTokenMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const saveConfig = useMutation({
    mutationFn: updateNotificationConfig,
    onSuccess: () => {
      setTokenMessage({ type: 'ok', text: 'Saved' });
      setTokenInput('');
      inval();
      setTimeout(() => setTokenMessage(null), 2500);
    },
    onError: (err: Error) => setTokenMessage({ type: 'err', text: err.message }),
  });

  // ─── Channel modal ──────────────────────────────────────────
  interface ChannelFormState {
    owner_admin_id: string;
    label: string;
    chat_id: string;
    enabled: boolean;
    is_forum: boolean;
    thread_reg: string;
    thread_dep: string;
    thread_cap: string;
    thread_ban: string;
    thread_summary: string;
    thread_daily: string;
    thread_offers: string;
    thread_updates: string;
    thread_releases: string;
    thread_lowreg: string;
    thread_localization: string;
    thread_deletions: string;
    cap_threshold_pct: string;
  }
  const emptyChannelForm: ChannelFormState = {
    owner_admin_id: '',
    label: '', chat_id: '', enabled: true,
    is_forum: false, thread_reg: '', thread_dep: '', thread_cap: '', thread_ban: '', thread_summary: '', thread_daily: '', thread_offers: '', thread_updates: '', thread_releases: '', thread_lowreg: '', thread_localization: '', thread_deletions: '',
    cap_threshold_pct: '80',
  };

  const [channelModal, setChannelModal] = useState<{ mode: 'create' | 'edit'; channel?: NotificationChannel } | null>(null);
  const [channelForm, setChannelForm] = useState<ChannelFormState>(emptyChannelForm);
  const [channelError, setChannelError] = useState('');

  const openCreateChannel = () => {
    setChannelForm(emptyChannelForm);
    setChannelError('');
    setChannelModal({ mode: 'create' });
  };
  const openEditChannel = (ch: NotificationChannel) => {
    setChannelForm({
      owner_admin_id: ch.owner_admin_id ?? '',
      label: ch.label,
      chat_id: ch.chat_id,
      enabled: !!ch.enabled,
      is_forum: !!ch.is_forum,
      thread_reg: ch.thread_reg != null ? String(ch.thread_reg) : '',
      thread_dep: ch.thread_dep != null ? String(ch.thread_dep) : '',
      thread_cap: ch.thread_cap != null ? String(ch.thread_cap) : '',
      thread_ban: ch.thread_ban != null ? String(ch.thread_ban) : '',
      thread_summary: ch.thread_summary != null ? String(ch.thread_summary) : '',
      thread_daily: ch.thread_daily != null ? String(ch.thread_daily) : '',
      thread_offers: ch.thread_offers != null ? String(ch.thread_offers) : '',
      thread_updates: ch.thread_updates != null ? String(ch.thread_updates) : '',
      thread_releases: ch.thread_releases != null ? String(ch.thread_releases) : '',
      thread_lowreg: ch.thread_lowreg != null ? String(ch.thread_lowreg) : '',
      thread_localization: ch.thread_localization != null ? String(ch.thread_localization) : '',
      thread_deletions: ch.thread_deletions != null ? String(ch.thread_deletions) : '',
      cap_threshold_pct: String(ch.cap_threshold_pct ?? 80),
    });
    setChannelError('');
    setChannelModal({ mode: 'edit', channel: ch });
  };

  const createChannelMut = useMutation({
    mutationFn: createNotificationChannel,
    onSuccess: () => { setChannelModal(null); inval(); },
    onError: (err: Error) => setChannelError(err.message),
  });
  const updateChannelMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateNotificationChannel>[1] }) =>
      updateNotificationChannel(id, data),
    onSuccess: () => { setChannelModal(null); inval(); },
    onError: (err: Error) => setChannelError(err.message),
  });
  const deleteChannelMut = useMutation({
    mutationFn: deleteNotificationChannel,
    onSuccess: () => inval(),
  });
  const toggleChannelMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => updateNotificationChannel(id, { enabled }),
    onSuccess: () => inval(),
  });

  const parseThread = (s: string): number | null => {
    const v = s.trim();
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  };

  const submitChannel = (e: React.FormEvent) => {
    e.preventDefault();
    setChannelError('');
    if (!channelForm.label.trim() || !channelForm.chat_id.trim()) {
      setChannelError('Label and chat ID are required');
      return;
    }
    let threshold = Number(channelForm.cap_threshold_pct);
    if (!Number.isFinite(threshold)) threshold = 80;
    threshold = Math.max(1, Math.min(100, Math.floor(threshold)));

    const base = {
      label: channelForm.label.trim(),
      chat_id: channelForm.chat_id.trim(),
      enabled: channelForm.enabled,
      is_forum: channelForm.is_forum,
      thread_reg: channelForm.is_forum ? parseThread(channelForm.thread_reg) : null,
      thread_dep: channelForm.is_forum ? parseThread(channelForm.thread_dep) : null,
      thread_cap: channelForm.is_forum ? parseThread(channelForm.thread_cap) : null,
      thread_ban: channelForm.is_forum ? parseThread(channelForm.thread_ban) : null,
      thread_summary: channelForm.is_forum ? parseThread(channelForm.thread_summary) : null,
      thread_daily: channelForm.is_forum ? parseThread(channelForm.thread_daily) : null,
      thread_offers: channelForm.is_forum ? parseThread(channelForm.thread_offers) : null,
      thread_updates: channelForm.is_forum ? parseThread(channelForm.thread_updates) : null,
      thread_releases: channelForm.is_forum ? parseThread(channelForm.thread_releases) : null,
      thread_lowreg: channelForm.is_forum ? parseThread(channelForm.thread_lowreg) : null,
      thread_localization: channelForm.is_forum ? parseThread(channelForm.thread_localization) : null,
      thread_deletions: channelForm.is_forum ? parseThread(channelForm.thread_deletions) : null,
      cap_threshold_pct: threshold,
      owner_admin_id: isSuper ? (channelForm.owner_admin_id || null) : undefined,
    };

    if (channelModal?.mode === 'create') {
      createChannelMut.mutate(base);
    } else if (channelModal?.mode === 'edit' && channelModal.channel) {
      updateChannelMut.mutate({ id: channelModal.channel.id, data: base });
    }
  };

  // ─── Rule modal ─────────────────────────────────────────────
  const [ruleModal, setRuleModal] = useState<{ mode: 'create' | 'edit'; rule?: NotificationRule } | null>(null);
  const [ruleForm, setRuleForm] = useState<{ event_type: NotificationRule['event_type']; channel_id: string; enabled: boolean }>({
    event_type: 'dep',
    channel_id: '',
    enabled: true,
  });
  const [ruleError, setRuleError] = useState('');

  const openCreateRule = () => {
    setRuleForm({ event_type: 'dep', channel_id: channels[0]?.id || '', enabled: true });
    setRuleError('');
    setRuleModal({ mode: 'create' });
  };
  const openEditRule = (r: NotificationRule) => {
    setRuleForm({ event_type: r.event_type, channel_id: r.channel_id, enabled: !!r.enabled });
    setRuleError('');
    setRuleModal({ mode: 'edit', rule: r });
  };

  const createRuleMut = useMutation({
    mutationFn: createNotificationRule,
    onSuccess: () => { setRuleModal(null); inval(); },
    onError: (err: Error) => setRuleError(err.message),
  });
  const updateRuleMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateNotificationRule>[1] }) =>
      updateNotificationRule(id, data),
    onSuccess: () => { setRuleModal(null); inval(); },
    onError: (err: Error) => setRuleError(err.message),
  });
  const deleteRuleMut = useMutation({
    mutationFn: deleteNotificationRule,
    onSuccess: () => inval(),
  });
  const toggleRuleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => updateNotificationRule(id, { enabled }),
    onSuccess: () => inval(),
  });

  const submitRule = (e: React.FormEvent) => {
    e.preventDefault();
    setRuleError('');
    if (!ruleForm.channel_id) {
      setRuleError('Pick a channel');
      return;
    }
    if (ruleModal?.mode === 'create') {
      createRuleMut.mutate({
        event_type: ruleForm.event_type,
        channel_id: ruleForm.channel_id,
        enabled: ruleForm.enabled,
      });
    } else if (ruleModal?.mode === 'edit' && ruleModal.rule) {
      updateRuleMut.mutate({
        id: ruleModal.rule.id,
        data: {
          event_type: ruleForm.event_type,
          channel_id: ruleForm.channel_id,
          enabled: ruleForm.enabled,
        },
      });
    }
  };

  // ─── Test send ──────────────────────────────────────────────
  const [testStatus, setTestStatus] = useState<Record<string, { ok: boolean; text: string }>>({});
  const testMut = useMutation({
    mutationFn: testNotificationChannel,
    onMutate: ({ channel_id }) => {
      setTestStatus((p) => ({ ...p, [channel_id]: { ok: true, text: 'Sending…' } }));
    },
    onSuccess: (_data, vars) => {
      setTestStatus((p) => ({ ...p, [vars.channel_id]: { ok: true, text: 'Test sent ✓' } }));
      setTimeout(() => setTestStatus((p) => { const n = { ...p }; delete n[vars.channel_id]; return n; }), 4000);
    },
    onError: (err: Error, vars) => {
      setTestStatus((p) => ({ ...p, [vars.channel_id]: { ok: false, text: err.message } }));
    },
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-content">Notifications</h1>
        <p className="text-sm text-content-subtle mt-1">
          Telegram channels and routing rules for tracker postbacks. CAP from offers is appended automatically.
        </p>
      </div>

      {/* Bot token + master switch */}
      {isSuper && <section className="bg-surface border border-border rounded-lg p-4 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-content">Telegram bot</h2>
            <p className="text-xs text-content-subtle mt-1">
              Bot token from <code className="px-1 py-0.5 bg-surface-muted rounded">@BotFather</code>. Add the bot to each target chat/channel as admin.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={config?.enabled ?? true}
              onChange={(e) => saveConfig.mutate({ enabled: e.target.checked })}
              className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            />
            <span className="text-sm text-content-muted">Notifications enabled</span>
          </label>
        </div>

        <div className="mt-3 flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-xs font-medium text-content-muted mb-1">Bot token</label>
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder={config?.has_token ? `current: ${config.token_preview} — type new value to replace` : 'e.g. 1234567890:AAH...'}
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
            />
          </div>
          <Button
            onClick={() => saveConfig.mutate({ bot_token: tokenInput })}
            disabled={!tokenInput.trim()}
            loading={saveConfig.isPending}
          >
            Save token
          </Button>
          {config?.has_token && (
            <Button
              variant="secondary"
              onClick={() => {
                if (confirm('Remove the bot token? Notifications will stop working until a new token is set.')) {
                  saveConfig.mutate({ bot_token: '' });
                }
              }}
            >
              Remove
            </Button>
          )}
        </div>
        {tokenMessage && (
          <p className={`mt-2 text-xs ${tokenMessage.type === 'ok' ? 'text-success' : 'text-danger'}`}>{tokenMessage.text}</p>
        )}
      </section>}

      {isSuper && <AssistantSettings />}

      {/* Channels */}
      <section className="bg-surface border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-content">Channels</h2>
            <p className="text-xs text-content-subtle">Named destinations: chat IDs (channel/group/user) the bot posts to.</p>
          </div>
          <EditOnly><Button onClick={openCreateChannel}>+ Add channel</Button></EditOnly>
        </div>

        {channels.length === 0 ? (
          <div className="text-sm text-content-subtle text-center border border-dashed border-border rounded-md py-6">
            No channels yet. Create one and bind it via a rule below.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-content-subtle uppercase">
                <tr>
                  <th className="text-left px-2 py-2">Label</th>
                  <th className="text-left px-2 py-2">Chat ID</th>
                  <th className="text-left px-2 py-2">Scope</th>
                  <th className="text-left px-2 py-2">Kind</th>
                  <th className="text-left px-2 py-2">Enabled</th>
                  <th className="text-right px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {channels.map((ch) => (
                  <tr key={ch.id}>
                    <td className="px-2 py-2 font-medium text-content">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{ch.label}</span>
                        {ch.is_forum ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">FORUM</span>
                        ) : null}
                      </div>
                      {ch.is_forum && (
                        <div className="mt-1 text-[11px] text-content-subtle font-mono space-x-2">
                          <span>reg:{ch.thread_reg ?? '—'}</span>
                          <span>dep:{ch.thread_dep ?? '—'}</span>
                          <span>cap:{ch.thread_cap ?? '—'}</span>
                          <span>ban:{ch.thread_ban ?? '—'}</span>
                          <span>summary:{ch.thread_summary ?? '—'}</span>
                          <span>daily:{ch.thread_daily ?? '—'}</span>
                          <span>offers:{ch.thread_offers ?? '—'}</span>
                          <span>updates:{ch.thread_updates ?? '—'}</span>
                          <span>releases:{ch.thread_releases ?? '—'}</span>
                          <span>lowreg:{ch.thread_lowreg ?? '—'}</span>
                          <span>loc:{ch.thread_localization ?? '—'}</span>
                          <span>del:{ch.thread_deletions ?? '—'}</span>
                          <span className="text-content-subtle">≥{ch.cap_threshold_pct}%</span>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs text-content-muted">{ch.chat_id}</td>
                    <td className="px-2 py-2 text-xs text-content-muted">
                      {ch.owner_admin_id ? (
                        <div>
                          <div className="font-medium text-content">{ch.owner_name || ch.owner_email || 'Channel owner'}</div>
                          <div className="text-content-subtle">
                            {ch.scope_mode === 'owner_campaigns' ? 'Assigned + self-created campaigns' : 'All current and future assigned apps'}
                          </div>
                        </div>
                      ) : ch.scope_mode === 'legacy_apps' ? (
                        <div>
                          <div className="text-warning">Legacy app filter</div>
                          <div className="text-content-subtle">Edit and choose an ASO owner</div>
                        </div>
                      ) : (
                        <span className="text-content-subtle">Global · all apps</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs text-content-subtle">{ch.kind}</td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        onClick={() => toggleChannelMut.mutate({ id: ch.id, enabled: !ch.enabled })}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${ch.enabled ? 'bg-primary' : 'bg-border-strong'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-surface transition ${ch.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => testMut.mutate({ channel_id: ch.id })}
                        className="text-xs text-primary hover:underline mr-3"
                        disabled={testMut.isPending}
                      >
                        Test
                      </button>
                      {testStatus[ch.id] && (
                        <span className={`text-xs mr-3 ${testStatus[ch.id].ok ? 'text-success' : 'text-danger'}`}>{testStatus[ch.id].text}</span>
                      )}
                      <EditOnly>
                        <button onClick={() => openEditChannel(ch)} className="text-xs text-content-subtle hover:text-content mr-3">Edit</button>
                        <button
                          onClick={() => {
                            if (confirm(`Delete channel "${ch.label}"? Linked rules will also be removed.`)) {
                              deleteChannelMut.mutate(ch.id);
                            }
                          }}
                          className="text-xs text-danger hover:text-danger"
                        >
                          Delete
                        </button>
                      </EditOnly>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Rules */}
      <section className="bg-surface border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-content">Routing rules</h2>
            <p className="text-xs text-content-subtle">
              When tracker postback arrives with status X — send to channel Y. <span className="text-content-subtle">Forum channels route automatically by topic and don't need rules.</span>
            </p>
          </div>
          <EditOnly><Button onClick={openCreateRule} disabled={channels.filter((c) => !c.is_forum).length === 0}>+ Add rule</Button></EditOnly>
        </div>

        {rules.length === 0 ? (
          <div className="text-sm text-content-subtle text-center border border-dashed border-border rounded-md py-6">
            {channels.length === 0
              ? 'Create a channel first, then add a rule.'
              : 'No rules yet. Create one to start receiving notifications.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-content-subtle uppercase">
                <tr>
                  <th className="text-left px-2 py-2">Event</th>
                  <th className="text-left px-2 py-2">Channel</th>
                  <th className="text-left px-2 py-2">Chat ID</th>
                  <th className="text-left px-2 py-2">Enabled</th>
                  <th className="text-right px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rules.map((r) => (
                  <tr key={r.id}>
                    <td className="px-2 py-2 text-content">{EVENT_LABELS[r.event_type]}</td>
                    <td className="px-2 py-2 font-medium">{r.channel_label || <span className="text-danger">(missing)</span>}</td>
                    <td className="px-2 py-2 font-mono text-xs text-content-muted">{r.channel_chat_id || '—'}</td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        onClick={() => toggleRuleMut.mutate({ id: r.id, enabled: !r.enabled })}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${r.enabled ? 'bg-primary' : 'bg-border-strong'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-surface transition ${r.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <EditOnly>
                        <button onClick={() => openEditRule(r)} className="text-xs text-content-subtle hover:text-content mr-3">Edit</button>
                        <button
                          onClick={() => {
                            if (confirm('Delete this rule?')) deleteRuleMut.mutate(r.id);
                          }}
                          className="text-xs text-danger hover:text-danger"
                        >
                          Delete
                        </button>
                      </EditOnly>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Channel modal */}
      <Modal
        isOpen={!!channelModal}
        onClose={() => setChannelModal(null)}
        title={channelModal?.mode === 'create' ? 'Add channel' : 'Edit channel'}
      >
        <form onSubmit={submitChannel} className="space-y-4">
          {channelError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{channelError}</div>
          )}
          {isSuper && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Channel owner</label>
              <Select
                value={channelForm.owner_admin_id}
                onChange={(value) => setChannelForm((prev) => ({ ...prev, owner_admin_id: value }))}
                options={[
                  { value: '', label: 'Global admin channel (all apps)' },
                  ...appOwners
                    .filter((user) => user.role !== 'super_admin' && (user.user_type === 'aso' || user.user_type === 'traffic') && !user.assistant_of_admin_id && user.is_active)
                    .map((user) => ({ value: user.id, label: user.name || user.email })),
                ]}
              />
              <p className="mt-1 text-xs text-content-subtle">
                {channelForm.owner_admin_id
                  ? 'ASO channels follow assigned apps; traffic channels follow assigned and self-created campaigns.'
                  : 'Global admin channel receives events from every app.'}
              </p>
            </div>
          )}
          {!isSuper && (
            <div className="rounded-md border border-border bg-surface-muted px-3 py-2 text-xs text-content-muted">
              {admin?.user_type === 'traffic'
                ? 'This channel automatically follows your assigned and self-created campaigns.'
                : 'This channel automatically follows all your current and future apps.'}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Label</label>
            <input
              type="text"
              value={channelForm.label}
              onChange={(e) => setChannelForm((p) => ({ ...p, label: e.target.value }))}
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="e.g. Deps — main"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Chat ID</label>
            <input
              type="text"
              value={channelForm.chat_id}
              onChange={(e) => setChannelForm((p) => ({ ...p, chat_id: e.target.value }))}
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
              placeholder="-1001234567890 or @public_channel or 123456789"
            />
            <p className="mt-1 text-xs text-content-subtle">
              Numeric chat IDs (groups/channels start with -100). Use <code>@username</code> for public channels. The bot must be a member of the chat.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={channelForm.enabled}
              onChange={(e) => setChannelForm((p) => ({ ...p, enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            />
            <span className="text-sm text-content-muted">Enabled</span>
          </label>

          <div className="border-t border-border pt-4">
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={channelForm.is_forum}
                onChange={(e) => setChannelForm((p) => ({ ...p, is_forum: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
              />
              <span className="text-sm">
                <span className="font-medium text-content">Super-chat with topics (forum)</span>
                <span className="block text-xs text-content-subtle mt-0.5">
                  One supergroup, seven threads: <b>regs</b>, <b>deps</b>, <b>caps</b>, <b>bans</b>, <b>summary</b>, <b>daily</b>, <b>offer changes</b>. Deposits where the offer cap is ≥ threshold are mirrored into the caps thread. Bans are posted from the apps refresh-versions check. Summary fires once per offer when its reg count crosses the configured threshold (default 30). Daily posts a rolling last-24h digest (regs/deps by app and country) once a day. Offer changes fire whenever an offer under a geo is attached, detached, swapped, deleted, or has its URL changed inside a tracker campaign. Routing rules below are ignored for forum channels.
                </span>
              </span>
            </label>

            {channelForm.is_forum && (
              <div className="mt-3 space-y-3 pl-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Thread ID — regs</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_reg}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_reg: e.target.value }))}
                      placeholder="e.g. 12"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Thread ID — deps</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_dep}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_dep: e.target.value }))}
                      placeholder="e.g. 14"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Thread ID — caps</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_cap}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_cap: e.target.value }))}
                      placeholder="e.g. 16"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Thread ID — bans</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_ban}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_ban: e.target.value }))}
                      placeholder="e.g. 18"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Thread ID — summary</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_summary}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_summary: e.target.value }))}
                      placeholder="e.g. 20"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Thread ID — daily</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_daily}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_daily: e.target.value }))}
                      placeholder="e.g. 20"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Thread ID — offer changes</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_offers}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_offers: e.target.value }))}
                      placeholder="e.g. 22"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Updates topic</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_updates}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_updates: e.target.value }))}
                      placeholder="e.g. 24"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Releases topic</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_releases}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_releases: e.target.value }))}
                      placeholder="e.g. 24"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Low click→reg topic</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_lowreg}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_lowreg: e.target.value }))}
                      placeholder="e.g. 24"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Localization topic</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_localization}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_localization: e.target.value }))}
                      placeholder="e.g. 24"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Deletion requests topic</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={channelForm.thread_deletions}
                      onChange={(e) => setChannelForm((p) => ({ ...p, thread_deletions: e.target.value }))}
                      placeholder="e.g. 24"
                      className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-content-muted mb-1">
                    Cap threshold (%): mirror deposit when offer cap is filled by at least this much
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={channelForm.cap_threshold_pct}
                    onChange={(e) => setChannelForm((p) => ({ ...p, cap_threshold_pct: e.target.value }))}
                    className="block w-32 px-3 py-2 border border-border-strong rounded-md text-sm"
                  />
                  <p className="mt-1 text-xs text-content-subtle">
                    Default <b>80</b> = remaining ≤ 20% to fill the cap.
                  </p>
                </div>
                <p className="text-xs text-content-subtle">
                  How to find a thread ID: in the supergroup, open the topic and look at the URL or right-click any message → "Copy link"; the trailing number is the thread id. Empty thread = events for that topic are skipped.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setChannelModal(null)}>Cancel</Button>
            <Button type="submit" loading={createChannelMut.isPending || updateChannelMut.isPending}>
              {channelModal?.mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Rule modal */}
      <Modal
        isOpen={!!ruleModal}
        onClose={() => setRuleModal(null)}
        title={ruleModal?.mode === 'create' ? 'Add rule' : 'Edit rule'}
      >
        <form onSubmit={submitRule} className="space-y-4">
          {ruleError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{ruleError}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Event type</label>
            <Select
              value={ruleForm.event_type}
              onChange={(v) => setRuleForm((p) => ({ ...p, event_type: v as NotificationRule['event_type'] }))}
              options={[
                { value: 'dep', label: EVENT_LABELS.dep },
                { value: 'reg', label: EVENT_LABELS.reg },
                { value: 'all', label: EVENT_LABELS.all },
                { value: 'ban', label: EVENT_LABELS.ban },
                { value: 'summary', label: EVENT_LABELS.summary },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Channel</label>
            <Select
              value={ruleForm.channel_id}
              onChange={(v) => setRuleForm((p) => ({ ...p, channel_id: v }))}
              options={channels.filter((c) => !c.is_forum).map((c) => ({ value: c.id, label: `${c.label} (${c.chat_id})` }))}
              searchable
            />
            <p className="mt-1 text-xs text-content-subtle">Forum channels are excluded — they route by topic automatically.</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={ruleForm.enabled}
              onChange={(e) => setRuleForm((p) => ({ ...p, enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            />
            <span className="text-sm text-content-muted">Enabled</span>
          </label>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setRuleModal(null)}>Cancel</Button>
            <Button type="submit" loading={createRuleMut.isPending || updateRuleMut.isPending}>
              {ruleModal?.mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
